import { useCallback, useEffect, useState } from "react";
import type { AgentFlowSlotDTO, CreateAgentFlowSlotRequest } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ChipsInput } from "@/components/ui/ChipsInput";

type SlotType = "text" | "choice" | "number";
type SlotSource = "ask" | "channel_phone" | "channel_name";

/**
 * Account-scoped slot catalog (ADR 0030). A business's data dictionary — define
 * a fact once (phone, service_address, move_type) and reference it from any flow
 * via `{ ref: key }`. This page manages the definitions; referencing happens in
 * the flow editor's Slots tab.
 */
export function SlotCatalogPage() {
  const [slots, setSlots] = useState<AgentFlowSlotDTO[]>([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<AgentFlowSlotDTO | "new" | null>(null);
  const [confirm, setConfirm] = useState<AgentFlowSlotDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      setSlots(await api.agentflow.listSlots());
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Couldn't load the slot catalog.");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(slot: AgentFlowSlotDTO) {
    setDeleting(true);
    try {
      await api.agentflow.deleteSlot(slot.key);
      setConfirm(null);
      await load();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Couldn't delete the slot.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">Slot catalog</h1>
          <p className="mt-1 text-[13px] text-zinc-500">
            Define a fact once and reuse it across every flow. Reference a catalog slot from a
            flow&rsquo;s Slots tab; edits here apply on each flow&rsquo;s next run.
          </p>
        </div>
        <Button size="sm" onClick={() => setEditing("new")}>
          New slot
        </Button>
      </div>

      {err && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {err}
        </div>
      )}

      {busy ? (
        <p className="text-[13px] text-zinc-500">Loading…</p>
      ) : slots.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 p-8 text-center text-[13px] text-zinc-500 dark:border-zinc-700">
          No catalog slots yet. Create one to reuse it across flows.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm ring-1 ring-black/[0.02] dark:border-zinc-800 dark:bg-zinc-900 dark:ring-white/[0.02]">
          <table className="w-full text-left text-[13px]">
            <thead className="border-b border-zinc-200 bg-zinc-50/80 text-[11px] uppercase tracking-wide text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/60">
              <tr>
                <th className="px-4 py-2.5 font-medium">Key</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium">Source</th>
                <th className="px-4 py-2.5 font-medium">Used by</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/70">
              {slots.map((s) => (
                <tr
                  key={s.key}
                  className="group transition-colors hover:bg-brand-50/40 dark:hover:bg-brand-500/[0.06]"
                >
                  <td className="px-4 py-3">
                    <div className="font-mono font-medium text-zinc-900 dark:text-zinc-100">
                      {s.key}
                    </div>
                    <div className="max-w-[320px] truncate text-[11.5px] text-zinc-500">
                      {s.prompt}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[11px] font-medium capitalize text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                        {s.type}
                      </span>
                      {s.type === "choice" && s.options && s.options.length > 0 && (
                        <OptionChips options={s.options} />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300">{s.source}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300">
                    {s.usageCount} flow{s.usageCount === 1 ? "" : "s"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      className="text-[12px] font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
                      onClick={() => setEditing(s)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="ml-3 text-[12px] text-zinc-400 hover:text-red-600"
                      onClick={() => setConfirm(s)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <SlotEditor
          slot={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {confirm && (
        <ConfirmDialog
          open
          title={`Delete slot "${confirm.key}"?`}
          description={
            confirm.usageCount > 0
              ? `This slot is referenced by ${confirm.usageCount} flow(s). Remove those references first.`
              : "This can't be undone."
          }
          confirmLabel="Delete"
          intent="danger"
          busy={deleting}
          onConfirm={() => void remove(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

/**
 * Renders choice options as chips. Shows the first 3; the rest collapse into a
 * "+N" chip with a native tooltip listing every option.
 */
function OptionChips({ options }: { options: string[] }) {
  const MAX = 3;
  const shown = options.slice(0, MAX);
  const rest = options.slice(MAX);
  return (
    <>
      {shown.map((o) => (
        <span
          key={o}
          className="inline-flex max-w-[140px] items-center truncate rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
          title={o}
        >
          {o}
        </span>
      ))}
      {rest.length > 0 && (
        <span
          className="inline-flex cursor-default items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
          title={rest.join(", ")}
        >
          +{rest.length}
        </span>
      )}
    </>
  );
}

const inputCls =
  "block w-full rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-[13px] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100";

function SlotEditor({
  slot,
  onClose,
  onSaved,
}: {
  slot: AgentFlowSlotDTO | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = slot === null;
  const [key, setKey] = useState(slot?.key ?? "");
  const [prompt, setPrompt] = useState(slot?.prompt ?? "");
  const [type, setType] = useState<SlotType>(slot?.type ?? "text");
  const [source, setSource] = useState<SlotSource>(slot?.source ?? "ask");
  const [options, setOptions] = useState<string[]>(slot?.options ?? []);
  const [format, setFormat] = useState<"" | "phone" | "email">(slot?.format ?? "");
  const [min, setMin] = useState(slot?.min?.toString() ?? "");
  const [max, setMax] = useState(slot?.max?.toString() ?? "");
  const [askExamples, setAskExamples] = useState<{ agent: string; customer?: string }[]>(
    slot?.askExamples ?? [],
  );
  const [genIdx, setGenIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Channel-sourced slots are auto-filled, so the agent CONFIRMS rather than asks.
  const isConfirm = source !== "ask";

  async function generateCustomer(i: number, agentLine: string) {
    const agent = agentLine.trim();
    if (!agent) return;
    setGenIdx(i);
    try {
      const { customer } = await api.agentflow.generateExampleCustomer({
        agent,
        slotPrompts: prompt.trim() ? [prompt.trim()] : [],
      });
      setAskExamples((xs) => xs.map((x, j) => (j === i ? { ...x, customer } : x)));
    } catch {
      /* best-effort */
    } finally {
      setGenIdx(null);
    }
  }

  async function save() {
    setSaving(true);
    setErr(null);
    const body: CreateAgentFlowSlotRequest = {
      key: key.trim(),
      prompt: prompt.trim(),
      type,
      source,
      ...(type === "choice" ? { options } : {}),
      ...(type === "text" && format ? { format } : {}),
      ...(type === "number" && min !== "" ? { min: Number(min) } : {}),
      ...(type === "number" && max !== "" ? { max: Number(max) } : {}),
      ...(askExamples.filter((e) => e.agent.trim()).length > 0
        ? { askExamples: askExamples.filter((e) => e.agent.trim()) }
        : {}),
    };
    try {
      if (isNew) await api.agentflow.createSlot(body);
      else {
        const { key: _key, ...rest } = body;
        void _key;
        await api.agentflow.updateSlot(slot.key, rest);
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Couldn't save the slot.");
    } finally {
      setSaving(false);
    }
  }

  const canSave = key.trim() !== "" && prompt.trim() !== "" && !saving;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-md space-y-3 overflow-y-auto rounded-xl border border-zinc-200 bg-white p-4 shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-[14px] font-semibold text-zinc-900 dark:text-zinc-100">
          {isNew ? "New catalog slot" : `Edit "${slot.key}"`}
        </h3>

        <label className="block space-y-1">
          <span className="text-[11px] font-medium text-zinc-500">Key</span>
          <input
            className={inputCls + " font-mono disabled:opacity-60"}
            value={key}
            disabled={!isNew}
            placeholder="service_address"
            onChange={(e) => setKey(e.target.value)}
          />
          {isNew && (
            <span className="text-[10.5px] text-zinc-400">
              Letters, numbers, underscores. This is how flows reference the slot; it can&rsquo;t
              change later.
            </span>
          )}
        </label>

        <label className="block space-y-1">
          <span className="text-[11px] font-medium text-zinc-500">Prompt (how the agent asks)</span>
          <input
            className={inputCls}
            value={prompt}
            placeholder="What's the service address?"
            onChange={(e) => setPrompt(e.target.value)}
          />
        </label>

        <div className="flex gap-2">
          <label className="flex-1 space-y-1">
            <span className="text-[11px] font-medium text-zinc-500">Type</span>
            <select
              className={inputCls}
              value={type}
              onChange={(e) => setType(e.target.value as SlotType)}
            >
              <option value="text">Text</option>
              <option value="choice">Choices</option>
              <option value="number">Number</option>
            </select>
          </label>
          <label className="flex-1 space-y-1">
            <span className="text-[11px] font-medium text-zinc-500">Source</span>
            <select
              className={inputCls}
              value={source}
              onChange={(e) => setSource(e.target.value as SlotSource)}
            >
              <option value="ask">Ask the customer</option>
              <option value="channel_phone">Customer phone</option>
              <option value="channel_name">WhatsApp name</option>
            </select>
          </label>
        </div>

        {type === "choice" && (
          <label className="block space-y-1">
            <span className="text-[11px] font-medium text-zinc-500">Options</span>
            <ChipsInput
              value={options}
              onChange={setOptions}
              placeholder="Type a value, press Enter…"
              title="Choice options"
            />
          </label>
        )}

        {type === "text" && (
          <label className="block space-y-1">
            <span className="text-[11px] font-medium text-zinc-500">Format</span>
            <select
              className={inputCls}
              value={format}
              onChange={(e) => setFormat(e.target.value as "" | "phone" | "email")}
            >
              <option value="">Free text</option>
              <option value="phone">Phone</option>
              <option value="email">Email</option>
            </select>
          </label>
        )}

        {type === "number" && (
          <div className="flex gap-2">
            <label className="flex-1 space-y-1">
              <span className="text-[11px] font-medium text-zinc-500">Min</span>
              <input
                type="number"
                className={inputCls}
                value={min}
                onChange={(e) => setMin(e.target.value)}
              />
            </label>
            <label className="flex-1 space-y-1">
              <span className="text-[11px] font-medium text-zinc-500">Max</span>
              <input
                type="number"
                className={inputCls}
                value={max}
                onChange={(e) => setMax(e.target.value)}
              />
            </label>
          </div>
        )}

        {/* How to ask / confirm — few-shot phrasings that travel with the slot
            (ADR 0029/0030). Author writes the agent line; we generate the customer. */}
        <fieldset className="space-y-1.5">
          <legend className="text-[11px] font-medium text-zinc-500">
            {isConfirm ? "How to confirm (examples)" : "How to ask (examples)"}
          </legend>
          {askExamples.map((ex, i) => (
            <div key={i} className="space-y-1">
              <div className="flex items-center gap-2">
                <input
                  className={inputCls + " flex-1"}
                  placeholder={
                    isConfirm
                      ? "Agent confirms… (e.g. I've got your number as +1 555 010 1234 — right?)"
                      : "Agent phrasing… (e.g. What's the best address for the crew?)"
                  }
                  value={ex.agent}
                  onChange={(e) =>
                    setAskExamples((xs) =>
                      xs.map((x, j) =>
                        j === i ? { ...x, agent: e.target.value, customer: undefined } : x,
                      ),
                    )
                  }
                  onBlur={(e) => {
                    if (e.target.value.trim() && !ex.customer)
                      void generateCustomer(i, e.target.value);
                  }}
                />
                <button
                  type="button"
                  className="shrink-0 text-[11px] text-zinc-400 hover:text-red-600"
                  onClick={() => setAskExamples((xs) => xs.filter((_, j) => j !== i))}
                >
                  remove
                </button>
              </div>
              {genIdx === i ? (
                <p className="text-[11px] text-zinc-400">Generating customer message…</p>
              ) : ex.customer ? (
                <div className="flex items-start gap-1.5 text-[11px] text-zinc-500">
                  <span className="shrink-0 rounded bg-zinc-100 px-1 py-0.5 text-[9.5px] font-medium uppercase tracking-wide text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                    customer · auto
                  </span>
                  <span className="min-w-0 flex-1">{ex.customer}</span>
                  <button
                    type="button"
                    className="shrink-0 text-brand-600 hover:text-brand-700 dark:text-brand-400"
                    onClick={() => void generateCustomer(i, ex.agent)}
                  >
                    redo
                  </button>
                </div>
              ) : ex.agent.trim() ? (
                <p className="text-[11px] text-zinc-400">
                  A customer message will be generated automatically.
                </p>
              ) : null}
            </div>
          ))}
          {askExamples.length < 3 && (
            <button
              type="button"
              className="text-[11px] text-brand-600 hover:text-brand-700 dark:text-brand-400"
              onClick={() => setAskExamples((xs) => [...xs, { agent: "" }])}
            >
              + Add example
            </button>
          )}
        </fieldset>

        {err && <p className="text-[12px] text-red-600 dark:text-red-400">{err}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void save()} disabled={!canSave}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
